// AIchat/src/components/ProfilePage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ArrowLeft, User, Mail, Calendar, DollarSign, BarChart3, Zap, Clock, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ProfilePage = () => {
  const { user, checkAuthStatus, logout } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || ''
      });
    }
    loadProfileData();
  }, [user]);

  const loadProfileData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/user/profile`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
      }
    } catch (error) {
      console.error('Failed to load profile data:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
        await checkAuthStatus();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update profile');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      username: user?.username || '',
      email: user?.email || ''
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  if (!user) return null;

  return (
    <div className="profile-page">
      <div className="profile-header">
        <button onClick={() => navigate('/')} className="back-btn">
          <ArrowLeft size={24} />
          <span>Back</span>
        </button>
        <h1>Profile</h1>
      </div>

      <div className="profile-content">
        <div className="profile-card">
          <div className="profile-avatar">
            {user.picture ? (
              <img src={user.picture} alt="Profile" className="avatar-img" />
            ) : (
              <div className="avatar-placeholder">
                <User size={40} />
              </div>
            )}
          </div>

          {success && (
            <div className="alert success">{success}</div>
          )}
          
          {error && (
            <div className="alert error">{error}</div>
          )}

          <div className="profile-info">
            <div className="info-group">
              <div className="info-icon">
                <User size={18} />
              </div>
              <div className="info-content">
                <label>Username</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                    className="edit-input"
                  />
                ) : (
                  <p>{user.username || 'Not set'}</p>
                )}
              </div>
            </div>

            <div className="info-group">
              <div className="info-icon">
                <Mail size={18} />
              </div>
              <div className="info-content">
                <label>Email</label>
                <p>{user.email}</p>
              </div>
            </div>

            <div className="info-group">
              <div className="info-icon">
                <DollarSign size={18} />
              </div>
              <div className="info-content">
                <label>Current Balance</label>
                <p className="balance-amount">
                  ${typeof user?.balance === 'number' ? user.balance.toFixed(4) : '0.0000'}
                </p>
              </div>
            </div>

            {profileData && (
              <>
                {profileData.membershipName && (
                  <div className="info-group">
                    <div className="info-icon">
                      <DollarSign size={18} />
                    </div>
                    <div className="info-content">
                      <label>Membership</label>
                      <p className="membership-name">{profileData.membershipName}</p>
                      {profileData.membershipExpires && (
                        <p className="membership-expires">
                          Expires: {new Date(profileData.membershipExpires).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="info-group">
                  <div className="info-icon">
                    <Zap size={18} />
                  </div>
                  <div className="info-content">
                    <label>Session Status</label>
                    <div className="session-info">
                      <p>Remaining: {profileData.sessionTokensRemaining || 0} tokens</p>
                      <p>Used: {profileData.sessionTokensUsed || 0} / {profileData.maxTokens || 10000}</p>
                    </div>
                  </div>
                </div>

                <div className="info-group">
                  <div className="info-icon">
                    <Clock size={18} />
                  </div>
                  <div className="info-content">
                    <label>Hourly Refresh</label>
                    <p>+{profileData.tokensPerHour || 500} tokens every hour</p>
                  </div>
                </div>
              </>
            )}

            <div className="info-group">
              <div className="info-icon">
                <BarChart3 size={18} />
              </div>
              <div className="info-content">
                <label>Usage Statistics</label>
                <div className="stats">
                  <p>Total Tokens: {(user.totalTokensUsed || 0).toLocaleString()}</p>
                  <p>Total Spent: ${typeof user?.totalCost === 'number' ? user.totalCost.toFixed(4) : '0.0000'}</p>
                </div>
              </div>
            </div>

            <div className="info-group">
              <div className="info-icon">
                <Calendar size={18} />
              </div>
              <div className="info-content">
                <label>Member Since</label>
                <p>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="profile-actions">
            {isEditing ? (
              <>
                <button onClick={handleSave} disabled={loading} className="btn btn-primary">
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button onClick={handleCancel} disabled={loading} className="btn btn-secondary">
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setIsEditing(true)} className="btn btn-primary">
                  Edit Profile
                </button>
                <button onClick={logout} className="btn btn-danger">
                  <LogOut size={18} />
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;